import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { fetchClients, IClients } from "./ClientService";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface IProfile {
  id: string;
  name: string;
  providerId: string;
  accountManagerEmail: string;
  isDeleted?: boolean;
  userId: string;
  clientInitialBalance: number;
  clientCreditLimit: number;
  providerCreditLimit: number;
  balance: string;
  paymentTerm: string;
  address: string;
  phoneNumber: string;
  countryId: string;
  currencyId: string;
  website?: string;
  logo?: File;
  ratingEmail: string;
  billingEmail: string;
  dailyReportEmail: string;
  supportEmail: string;
  invoiceTitle: string;
  emailAddress: string;
  sidRegistrationEmail: string;
  rateUpdatesEmail?: string;
  updatedBy?: string;
  clientBalance: number;
  clientAlert1: number;
  clientBalanceLimit: number;
  clientAlert2: number;
  flexAmount: number;
  clientAlert3: number;

  providerAlert1: number;
  providerAlert2: number;
  providerAlert3: number;
  providerInitialBalance: number;
  invoiceReference: string;
  invoiceTemplateId: string;

  paymentType: string;
  billingTerm: string;

  beneficiaryName: string;
  iban: string;
  bankName: string;
  swiftCode: string;
  bankAddress: string;
  bankPhoneNumber: string;
  accountNumber: string;
  createdBy?: string;
  usesVpn: boolean;
}

/** A profile as the Profile page lists it, with its balance (admin-api's ProfileDto). */
export interface IProfileWithBalance
  extends Pick<
    IProfile,
    | "id"
    | "name"
    | "userId"
    | "clientCreditLimit"
    | "providerCreditLimit"
    | "flexAmount"
    | "currencyId"
    | "paymentTerm"
  > {
  createdDate?: string;
  clientUsage?: number;
  vendorUsage?: number;
  balance?: number;
  creditStatus?: CreditStatus;
}

export interface IProfileHistory {
  id: string;
  snapshot: IProfile & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export interface ProfileHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: IProfile & { timestamp: string };
  diff?: Record<string, string>;
}

export const exportProfiles = async (
  params: Params<IProfile> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/profiles/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "profiles.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching profiles:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchProfiles(
  params: Params<IProfile>
): Promise<IPageable<IProfile>> {
  try {
    const response = await axiosInstance.get("/profiles", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching Profiles.`
    );
  }
}

export async function fetchProfile(id: IProfile["id"]): Promise<IProfile> {
  try {
    const response = await axiosInstance.get(`/profiles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting Profile/${id}.`
    );
  }
}

export const addProfileApi = async (data: Partial<IProfile>) => {
  try {
    const response = await axiosInstance.post(`/profiles`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while adding the Profile.`
    );
  }
};

export const editProfileApi = async (id: string, data: Partial<IProfile>) => {
  try {
    const response = await axiosInstance.put(`/profiles/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while updating the Profile.`
    );
  }
};

export const getProfileById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/profiles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the Profile.`
    );
  }
};

export const deleteProfileApi = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/profiles/${id}`);
  } catch (error: any) {
    let errorMessage = "Unable to delete profile.";

    if (isAxiosError(error)) {
      errorMessage =
        error.response?.data?.message || error.message || errorMessage;
    }

    throw new Error(errorMessage);
  }
};

export const useProfileQuery = (
  params: Params<IProfile>
): UseQueryResult<IPageable<IProfile>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };

  const queryKey = ["profile", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchProfiles(effectiveParams),
  });
};

/** Decided by the backend: using flex, or credit limit (and flex) used up. */
export type CreditStatus = "USING_FLEX" | "REACHED_LIMIT";

/** creditStatus: missing = all profiles */
export type ProfileWithBalanceParams = Params<IProfileWithBalance> & {
  creditStatus?: CreditStatus;
};

/** Profiles for the Profile page, each with its current balance. */
export async function fetchProfilesWithBalances(
  params: ProfileWithBalanceParams
): Promise<IPageable<IProfileWithBalance>> {
  try {
    const response = await axiosInstance.get("/profiles/with-balances", {
      params,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching Profiles.`
    );
  }
}

export const useProfilesWithBalancesQuery = (
  params: ProfileWithBalanceParams
): UseQueryResult<IPageable<IProfileWithBalance>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };

  const queryKey = ["profile", "with-balances", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchProfilesWithBalances(effectiveParams),
  });
};

export const useProfileByIdQuery = (
  id: string,
  options?: UseQueryOptions<IProfile, Error>
): UseQueryResult<IProfile, Error> => {
  return useQuery<IProfile, Error>({
    queryKey: ["profile", id],
    queryFn: () => fetchProfile(id),
    ...options,
  });
};

export const getCustomerNamesByProfileId = (profileId: string) => ({
  queryKey: ["customers", profileId],
  queryFn: async (): Promise<string[]> => {
    const clientsPage = await fetchClients({ profileId } as Params<IClients>);
    if (clientsPage.content && clientsPage.content.length > 0) {
      return clientsPage.content.map((client) => client.name);
    }
    return [];
  },
  select: (names: string[]) => names,
});

export const useCustomerByProfileIdQuery = (
  profileId: string,
  options?: UseQueryOptions<string[], Error>
): UseQueryResult<string[], Error> => {
  return useQuery({
    ...getCustomerNamesByProfileId(profileId),
    ...options,
  });
};

export async function fetchProfileHistory(
  id: IProfile["id"],
  params: Params<IProfileHistory> = {}
): Promise<IPageable<IProfile>> {
  try {
    const response = await axiosInstance.get(`/profiles/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the profiles history for ID ${id}.`
    );
  }
}
